import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { api } from '../services/api';
import LazyImage from '../components/LazyImage';
import { RotatingLines } from 'react-loader-spinner';
import { getImageUrl } from '../utils/imageUtils';

const Container = styled.div`
  max-width: ${({ theme }) => theme.breakpoints.large};
  margin: 0 auto;
  padding: 2rem;
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: ${({ theme }) => theme.colors.secondary};
  margin-bottom: 1rem;
`;

const InfoSection = styled.section`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  margin-bottom: 4rem;
  align-items: center;
  background: #f0f8ff;
  padding: 2rem;
  border-radius: 8px;
  
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
`;

const PostCard = styled.div`
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.small};
`;

const ImageContainer = styled.div`
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f0f0;
`;

const Content = styled.div`
  padding: 1.5rem;
`;

const Outreach = () => {
  const [header, setHeader] = useState(null);
  const [info, setInfo] = useState(null);
  const [posts, setPosts] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/outreach/header'),
      api.get('/outreach/info'),
      api.get('/outreach/posts'),
      api.get('/outreach/sections')
    ])
      .then(([h, i, p, s]) => {
        setHeader(h);
        setInfo(i);
        setPosts(p);
        setSections(s);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{display:'flex', justifyContent:'center', marginTop:'50px', minHeight:'100vh'}}>
      <RotatingLines strokeColor="grey" width="50" />
    </div>
  );

  return (
    <Container>
      {header && (
        <HeaderSection>
          <Title>{header.title}</Title>
          <p>{header.description}</p>
        </HeaderSection>
      )}

      {info && (
        <InfoSection>
           <div>
              <h2 style={{marginBottom:'1rem'}}>{info.title}</h2>
              <p style={{whiteSpace:'pre-wrap'}}>{info.description}</p>
           </div>
           {info.image && (
             <div style={{height: '250px', borderRadius: '8px', overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center', background:'#f0f0f0'}}>
                <LazyImage src={info.image} alt={info.title} objectFit="contain" />
             </div>
           )}
        </InfoSection>
      )}

      {posts.length > 0 && (
        <Grid style={{marginBottom: '3rem'}}>
          {posts.map(post => (
            <PostCard key={post._id}>
              {post.images && post.images.length > 0 && (
                <ImageContainer>
                  <LazyImage src={getImageUrl(post.images[0])} alt={post.title || ''} objectFit="contain" />
                </ImageContainer>
              )}
              {(post.title || post.description) && (
                <Content>
                  {post.title && <h3>{post.title}</h3>}
                  {post.description && <p>{post.description}</p>}
                </Content>
              )}
            </PostCard>
          ))}
        </Grid>
      )}

      {sections.map(section => (
        <div key={section._id} style={{marginBottom: '3rem'}}>
          {(section.title || section.description || section.image) && (
            <InfoSection>
              <div>
                {section.title && <h2 style={{marginBottom:'1rem'}}>{section.title}</h2>}
                {section.description && <p style={{whiteSpace:'pre-wrap'}}>{section.description}</p>}
              </div>
              {section.image && (
                <div style={{height: '250px', borderRadius: '8px', overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center', background:'#f0f0f0'}}>
                  <LazyImage src={getImageUrl(section.image)} alt={section.title || ''} objectFit="contain" />
                </div>
              )}
            </InfoSection>
          )}
          {section.posts && section.posts.length > 0 && (
            <Grid>
              {section.posts.map(post => (
                <PostCard key={post._id}>
                  {post.images && post.images.length > 0 && (
                    <ImageContainer>
                      <LazyImage src={getImageUrl(post.images[0])} alt={post.title || ''} />
                    </ImageContainer>
                  )}
                  {(post.title || post.description) && (
                    <Content>
                      {post.title && <h3>{post.title}</h3>}
                      {post.description && <p>{post.description}</p>}
                    </Content>
                  )}
                </PostCard>
              ))}
            </Grid>
          )}
        </div>
      ))}
    </Container>
  );
};

export default Outreach;
